import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './notificationsSettings.css';
import { useTranslation } from 'react-i18next';
import { 
    FaBell, 
    FaPlus, 
    FaEdit, 
    FaTrash, 
    FaSpinner,
    FaSearch,
    FaExclamationTriangle,
    FaEnvelope,
    FaCheck,
    FaTimes,
    FaCode,
    FaEye,
    FaEyeSlash,
    FaToggleOn,
    FaToggleOff
} from 'react-icons/fa';

const NotificationsSettings = () => {
    const [user, setUser] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [filteredNotifications, setFilteredNotifications] = useState([]);
    const [notificationTypes, setNotificationTypes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showActiveOnly, setShowActiveOnly] = useState(false);
    const [typeFilter, setTypeFilter] = useState('');
    const navigate = useNavigate();
    const { t } = useTranslation();

    // Create/Edit notification modal states
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedNotification, setSelectedNotification] = useState(null);
    const [notificationFormData, setNotificationFormData] = useState({ 
        name: '', 
        notification_type: '', 
        subject: '', 
        html_content: '', 
        description: '', 
        is_active: true 
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formError, setFormError] = useState(null);
    const [formSuccess, setFormSuccess] = useState(null);

    // Delete confirmation modal states
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [notificationToDelete, setNotificationToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Preview modal states
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const [previewNotification, setPreviewNotification] = useState(null);

    useEffect(() => {
        const token = localStorage.getItem('access_token');
        if (!token) {
            navigate('/');
            return;
        }

        const fetchData = async () => {
            try {
                setIsLoading(true);
                await Promise.all([
                    fetchUserData(token),
                    fetchNotifications(token),
                    fetchNotificationTypes(token)
                ]);
            } catch (error) {
                console.error('Error loading notifications data:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [navigate]);

    useEffect(() => {
        // Filter notifications based on search term, active filter, and type filter
        let filtered = notifications;
        
        if (showActiveOnly) {
            filtered = filtered.filter(notification => notification.is_active);
        }
        
        if (typeFilter) {
            filtered = filtered.filter(notification => notification.notification_type === typeFilter);
        }
        
        if (searchTerm) {
            filtered = filtered.filter(notification =>
                notification.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                notification.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (notification.description && notification.description.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }
        
        setFilteredNotifications(filtered);
    }, [notifications, searchTerm, showActiveOnly, typeFilter]);

    const fetchUserData = async (token) => {
        try {
            const response = await axios.get('/api/users/user', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUser(response.data);
        } catch (error) {
            console.error('Failed to fetch user data:', error);
            setUser(null);
            navigate('/');
        }
    };

    const fetchNotifications = async (token) => {
        try {
            const response = await axios.get('/api/notifications/html-content', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setNotifications(response.data || []);
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
            setNotifications([]);
        }
    };

    const fetchNotificationTypes = async (token) => {
        try {
            const response = await axios.get('/api/notifications/notification-types', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setNotificationTypes(response.data || []);
        } catch (error) {
            console.error('Failed to fetch notification types:', error);
            setNotificationTypes([]);
        }
    };

    const canManageNotifications = () => {
        return user && ['admin', 'seller', 'eshop'].includes(user.role);
    };

    const handleCreateNotification = () => {
        if (!canManageNotifications()) return;
        setNotificationFormData({ 
            name: '', 
            notification_type: '', 
            subject: '', 
            html_content: '', 
            description: '', 
            is_active: true 
        });
        setFormError(null);
        setFormSuccess(null);
        setIsCreateModalOpen(true);
    };

    const handleEditNotification = (notification) => {
        if (!canManageNotifications()) return;
        setSelectedNotification(notification);
        setNotificationFormData({ 
            name: notification.name, 
            notification_type: notification.notification_type, 
            subject: notification.subject, 
            html_content: notification.html_content,
            description: notification.description || '',
            is_active: notification.is_active
        });
        setFormError(null);
        setFormSuccess(null);
        setIsEditModalOpen(true);
    };

    const handleDeleteNotification = (notification) => {
        if (!canManageNotifications()) return;
        setNotificationToDelete(notification);
        setIsDeleteModalOpen(true);
    };

    const handlePreviewNotification = (notification) => {
        setPreviewNotification(notification);
        setIsPreviewModalOpen(true);
    };

    const handleFormInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setNotificationFormData(prev => ({ 
            ...prev, 
            [name]: type === 'checkbox' ? checked : value 
        }));
    };

    const handleSubmitCreate = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setFormError(null);
        setFormSuccess(null);

        const token = localStorage.getItem('access_token');

        try {
            const response = await axios.post('/api/notifications/html-content', notificationFormData, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setFormSuccess(t('notificationCreatedSuccessfully', 'Notification template created successfully!'));
            setNotificationFormData({ name: '', subject: '', html_content: '', description: '', is_active: true });
            await fetchNotifications(token);
            
            setTimeout(() => {
                setIsCreateModalOpen(false);
                setFormSuccess(null);
            }, 2000);
        } catch (error) {
            console.error('Failed to create notification:', error);
            setFormError(
                error.response?.data?.detail || 
                t('failedToCreateNotification', 'Failed to create notification template. Please try again.')
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmitEdit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setFormError(null);
        setFormSuccess(null);

        const token = localStorage.getItem('access_token');

        try {
            await axios.put(`/api/notifications/html-content/${selectedNotification.id}`, notificationFormData, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setFormSuccess(t('notificationUpdatedSuccessfully', 'Notification template updated successfully!'));
            await fetchNotifications(token);
            
            setTimeout(() => {
                setIsEditModalOpen(false);
                setFormSuccess(null);
                setSelectedNotification(null);
            }, 2000);
        } catch (error) {
            console.error('Failed to update notification:', error);
            setFormError(
                error.response?.data?.detail || 
                t('failedToUpdateNotification', 'Failed to update notification template. Please try again.')
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const confirmDelete = async () => {
        setIsDeleting(true);
        const token = localStorage.getItem('access_token');

        try {
            await axios.delete(`/api/notifications/html-content/${notificationToDelete.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            await fetchNotifications(token);
            setIsDeleteModalOpen(false);
            setNotificationToDelete(null);
        } catch (error) {
            console.error('Failed to delete notification:', error);
            alert(
                error.response?.data?.detail || 
                t('failedToDeleteNotification', 'Failed to delete notification template. Please try again.')
            );
        } finally {
            setIsDeleting(false);
        }
    };

    const toggleActiveStatus = async (notification) => {
        const token = localStorage.getItem('access_token');
        
        try {
            await axios.put(`/api/notifications/html-content/${notification.id}`, 
                { is_active: !notification.is_active }, 
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            await fetchNotifications(token);
        } catch (error) {
            console.error('Failed to toggle notification status:', error);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getNotificationTypeLabel = (type) => {
        switch (type) {
            case 'ticket_created':
                return t('ticketCreated', 'Ticket Created');
            case 'ticket_created_by_mail':
                return t('ticketCreatedByMail', 'Ticket Created by Mail');
            case 'response_received':
                return t('responseReceived', 'Response Received');
            default:
                return type;
        }
    };

    if (isLoading) {
        return (
            <div className="una-notifications-loading">
                <FaSpinner className="una-spinner" />
                <h2>{t('loadingNotifications', 'Loading notification templates...')}</h2>
            </div>
        );
    }

    return (
        <div className="una-notifications-container">
            <div className="una-notifications-header">
                <div className="una-notifications-title">
                    <FaBell className="una-notifications-icon" />
                    <h1>{t('notificationTemplates', 'Notification Templates')}</h1>
                </div>
                
                {canManageNotifications() && (
                    <button 
                        className="una-create-notification-button"
                        onClick={handleCreateNotification}
                    >
                        <FaPlus />
                        {t('createTemplate', 'Create Template')}
                    </button>
                )}
            </div>

            <div className="una-notifications-controls">
                <div className="una-search-container">
                    <FaSearch className="una-search-icon" />
                    <input
                        type="text"
                        placeholder={t('searchTemplates', 'Search templates...')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="una-search-input"
                    />
                </div>
                
                <div className="una-filter-container">
                    <select
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        className="una-type-filter"
                    >
                        <option value="">{t('allTypes', 'All Types')}</option>
                        {notificationTypes.map(type => (
                            <option key={type} value={type}>
                                {getNotificationTypeLabel(type)}
                            </option>
                        ))}
                    </select>
                    
                    <button
                        className={`una-filter-button ${showActiveOnly ? 'active' : ''}`}
                        onClick={() => setShowActiveOnly(!showActiveOnly)}
                    >
                        {showActiveOnly ? <FaToggleOn /> : <FaToggleOff />}
                        {t('activeOnly', 'Active Only')}
                    </button>
                </div>
            </div>

            <div className="una-notifications-content">
                {filteredNotifications.length === 0 ? (
                    <div className="una-no-notifications">
                        <FaBell className="una-no-notifications-icon" />
                        <h3>{t('noTemplates', 'No notification templates found')}</h3>
                        <p>{t('noTemplatesDescription', 'No templates match your search criteria.')}</p>
                    </div>
                ) : (
                    <div className="una-notifications-grid">
                        {filteredNotifications.map(notification => (
                            <div key={notification.id} className={`una-notification-card ${!notification.is_active ? 'inactive' : ''}`}>
                                <div className="una-notification-card-header">
                                    <div className="una-notification-title-section">
                                        <h3 className="una-notification-name">{notification.name}</h3>
                                        <div className="una-notification-type">
                                            <FaCode className="una-type-icon" />
                                            <span className="una-type-text">{getNotificationTypeLabel(notification.notification_type)}</span>
                                        </div>
                                        <div className="una-notification-status">
                                            {notification.is_active ? (
                                                <span className="una-status-active">
                                                    <FaCheck /> {t('active', 'Active')}
                                                </span>
                                            ) : (
                                                <span className="una-status-inactive">
                                                    <FaTimes /> {t('inactive', 'Inactive')}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    
                                    {canManageNotifications() && (
                                        <div className="una-notification-actions">
                                            <button
                                                className="una-preview-button"
                                                onClick={() => handlePreviewNotification(notification)}
                                                title={t('previewTemplate', 'Preview Template')}
                                            >
                                                <FaEye />
                                            </button>
                                            <button
                                                className="una-toggle-button"
                                                onClick={() => toggleActiveStatus(notification)}
                                                title={notification.is_active ? t('deactivate', 'Deactivate') : t('activate', 'Activate')}
                                            >
                                                {notification.is_active ? <FaEyeSlash /> : <FaEye />}
                                            </button>
                                            <button
                                                className="una-edit-button"
                                                onClick={() => handleEditNotification(notification)}
                                                title={t('editTemplate', 'Edit Template')}
                                            >
                                                <FaEdit />
                                            </button>
                                            <button
                                                className="una-delete-button"
                                                onClick={() => handleDeleteNotification(notification)}
                                                title={t('deleteTemplate', 'Delete Template')}
                                            >
                                                <FaTrash />
                                            </button>
                                        </div>
                                    )}
                                </div>
                                
                                <div className="una-notification-card-content">
                                    <div className="una-notification-subject">
                                        <FaEnvelope className="una-subject-icon" />
                                        <span className="una-subject-text">{notification.subject}</span>
                                    </div>
                                    
                                    {notification.description && (
                                        <p className="una-notification-description">{notification.description}</p>
                                    )}
                                    
                                    <div className="una-notification-meta">
                                        <span className="una-notification-date">
                                            {t('created', 'Created')}: {formatDate(notification.created_at)}
                                        </span>
                                        {notification.updated_at !== notification.created_at && (
                                            <span className="una-notification-date">
                                                {t('updated', 'Updated')}: {formatDate(notification.updated_at)}
                                            </span>
                                        )}
                                        <span className="una-notification-author">
                                            {t('by', 'By')}: {notification.created_by}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Create Notification Modal */}
            {isCreateModalOpen && (
                <div className="una-modal-overlay" onClick={() => setIsCreateModalOpen(false)}>
                    <div className="una-modal-content una-large-modal" onClick={(e) => e.stopPropagation()}>
                        <h2>{t('createNewTemplate', 'Create New Notification Template')}</h2>
                        <form onSubmit={handleSubmitCreate}>
                            <div className="una-form-row">
                                <div className="una-form-group">
                                    <label htmlFor="name">{t('templateName', 'Template Name')} *</label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={notificationFormData.name}
                                        onChange={handleFormInputChange}
                                        required
                                        maxLength={100}
                                        placeholder={t('enterTemplateName', 'Enter template name')}
                                    />
                                </div>
                                
                                <div className="una-form-group">
                                    <label htmlFor="notification_type">{t('notificationType', 'Notification Type')} *</label>
                                    <select
                                        id="notification_type"
                                        name="notification_type"
                                        value={notificationFormData.notification_type}
                                        onChange={handleFormInputChange}
                                        required
                                        className="una-type-select"
                                    >
                                        <option value="">{t('selectNotificationType', 'Select notification type')}</option>
                                        {notificationTypes.map(type => (
                                            <option key={type} value={type}>
                                                {getNotificationTypeLabel(type)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            
                            <div className="una-form-group">
                                <label htmlFor="subject">{t('emailSubject', 'Email Subject')} *</label>
                                <input
                                    type="text"
                                    id="subject"
                                    name="subject"
                                    value={notificationFormData.subject}
                                    onChange={handleFormInputChange}
                                    required
                                    maxLength={200}
                                    placeholder={t('enterEmailSubject', 'Enter email subject')}
                                />
                            </div>
                            
                            <div className="una-form-group">
                                <label htmlFor="description">{t('description', 'Description')}</label>
                                <textarea
                                    id="description"
                                    name="description"
                                    value={notificationFormData.description}
                                    onChange={handleFormInputChange}
                                    maxLength={500}
                                    rows="2"
                                    placeholder={t('enterTemplateDescription', 'Enter template description (optional)')}
                                />
                            </div>
                            
                            <div className="una-form-group">
                                <label htmlFor="html_content">{t('htmlContent', 'HTML Content')} *</label>
                                <textarea
                                    id="html_content"
                                    name="html_content"
                                    value={notificationFormData.html_content}
                                    onChange={handleFormInputChange}
                                    required
                                    rows="12"
                                    placeholder={t('enterHtmlContent', 'Enter HTML content for the email')}
                                    className="una-code-textarea"
                                />
                            </div>
                            
                            <div className="una-form-group">
                                <label className="una-checkbox-label">
                                    <input
                                        type="checkbox"
                                        name="is_active"
                                        checked={notificationFormData.is_active}
                                        onChange={handleFormInputChange}
                                    />
                                    {t('activeTemplate', 'Active Template')}
                                </label>
                            </div>

                            {formError && <p className="una-error-message">{formError}</p>}
                            {formSuccess && <p className="una-success-message">{formSuccess}</p>}

                            <div className="una-form-actions">
                                <button
                                    type="button"
                                    onClick={() => setIsCreateModalOpen(false)}
                                    className="una-button una-button-secondary"
                                >
                                    {t('cancel', 'Cancel')}
                                </button>
                                <button
                                    type="submit"
                                    className="una-button una-button-primary"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <FaSpinner className="una-spinner" />
                                            {t('creating', 'Creating...')}
                                        </>
                                    ) : (
                                        <>
                                            <FaPlus />
                                            {t('createTemplate', 'Create Template')}
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Notification Modal */}
            {isEditModalOpen && selectedNotification && (
                <div className="una-modal-overlay" onClick={() => setIsEditModalOpen(false)}>
                    <div className="una-modal-content una-large-modal" onClick={(e) => e.stopPropagation()}>
                        <h2>{t('editTemplate', 'Edit Notification Template')}</h2>
                        <form onSubmit={handleSubmitEdit}>
                            <div className="una-form-row">
                                <div className="una-form-group">
                                    <label htmlFor="edit-name">{t('templateName', 'Template Name')} *</label>
                                    <input
                                        type="text"
                                        id="edit-name"
                                        name="name"
                                        value={notificationFormData.name}
                                        onChange={handleFormInputChange}
                                        required
                                        maxLength={100}
                                        placeholder={t('enterTemplateName', 'Enter template name')}
                                    />
                                </div>
                                
                                <div className="una-form-group">
                                    <label htmlFor="edit-notification_type">{t('notificationType', 'Notification Type')} *</label>
                                    <select
                                        id="edit-notification_type"
                                        name="notification_type"
                                        value={notificationFormData.notification_type}
                                        onChange={handleFormInputChange}
                                        required
                                        className="una-type-select"
                                    >
                                        <option value="">{t('selectNotificationType', 'Select notification type')}</option>
                                        {notificationTypes.map(type => (
                                            <option key={type} value={type}>
                                                {getNotificationTypeLabel(type)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            
                            <div className="una-form-group">
                                <label htmlFor="edit-subject">{t('emailSubject', 'Email Subject')} *</label>
                                <input
                                    type="text"
                                    id="edit-subject"
                                    name="subject"
                                    value={notificationFormData.subject}
                                    onChange={handleFormInputChange}
                                    required
                                    maxLength={200}
                                    placeholder={t('enterEmailSubject', 'Enter email subject')}
                                />
                            </div>
                            
                            <div className="una-form-group">
                                <label htmlFor="edit-description">{t('description', 'Description')}</label>
                                <textarea
                                    id="edit-description"
                                    name="description"
                                    value={notificationFormData.description}
                                    onChange={handleFormInputChange}
                                    maxLength={500}
                                    rows="2"
                                    placeholder={t('enterTemplateDescription', 'Enter template description (optional)')}
                                />
                            </div>
                            
                            <div className="una-form-group">
                                <label htmlFor="edit-html_content">{t('htmlContent', 'HTML Content')} *</label>
                                <textarea
                                    id="edit-html_content"
                                    name="html_content"
                                    value={notificationFormData.html_content}
                                    onChange={handleFormInputChange}
                                    required
                                    rows="12"
                                    placeholder={t('enterHtmlContent', 'Enter HTML content for the email')}
                                    className="una-code-textarea"
                                />
                            </div>
                            
                            <div className="una-form-group">
                                <label className="una-checkbox-label">
                                    <input
                                        type="checkbox"
                                        name="is_active"
                                        checked={notificationFormData.is_active}
                                        onChange={handleFormInputChange}
                                    />
                                    {t('activeTemplate', 'Active Template')}
                                </label>
                            </div>

                            {formError && <p className="una-error-message">{formError}</p>}
                            {formSuccess && <p className="una-success-message">{formSuccess}</p>}

                            <div className="una-form-actions">
                                <button
                                    type="button"
                                    onClick={() => setIsEditModalOpen(false)}
                                    className="una-button una-button-secondary"
                                >
                                    {t('cancel', 'Cancel')}
                                </button>
                                <button
                                    type="submit"
                                    className="una-button una-button-primary"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <FaSpinner className="una-spinner" />
                                            {t('updating', 'Updating...')}
                                        </>
                                    ) : (
                                        <>
                                            <FaEdit />
                                            {t('updateTemplate', 'Update Template')}
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {isDeleteModalOpen && notificationToDelete && (
                <div className="una-modal-overlay" onClick={() => setIsDeleteModalOpen(false)}>
                    <div className="una-modal-content una-delete-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="una-delete-warning">
                            <FaExclamationTriangle className="una-warning-icon" />
                            <h2>{t('confirmDeleteTemplate', 'Confirm Delete Template')}</h2>
                        </div>
                        
                        <p>
                            {t('deleteTemplateConfirmation', 'Are you sure you want to delete the notification template')} 
                            <strong> "{notificationToDelete.name}"</strong>? 
                            {t('thisActionCannotBeUndone', ' This action cannot be undone.')}
                        </p>

                        <div className="una-form-actions">
                            <button
                                type="button"
                                onClick={() => setIsDeleteModalOpen(false)}
                                className="una-button una-button-secondary"
                            >
                                {t('cancel', 'Cancel')}
                            </button>
                            <button
                                onClick={confirmDelete}
                                className="una-button una-button-danger"
                                disabled={isDeleting}
                            >
                                {isDeleting ? (
                                    <>
                                        <FaSpinner className="una-spinner" />
                                        {t('deleting', 'Deleting...')}
                                    </>
                                ) : (
                                    <>
                                        <FaTrash />
                                        {t('deleteTemplate', 'Delete Template')}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Preview Modal */}
            {isPreviewModalOpen && previewNotification && (
                <div className="una-modal-overlay" onClick={() => setIsPreviewModalOpen(false)}>
                    <div className="una-modal-content una-preview-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="una-preview-header">
                            <h2>{t('previewTemplate', 'Preview Template')}: {previewNotification.name}</h2>
                            <button 
                                className="una-close-button"
                                onClick={() => setIsPreviewModalOpen(false)}
                            >
                                <FaTimes />
                            </button>
                        </div>
                        
                        <div className="una-preview-content">
                            <div className="una-preview-subject">
                                <strong>{t('subject', 'Subject')}: </strong>
                                {previewNotification.subject}
                            </div>
                            
                            <div className="una-preview-html">
                                <iframe
                                    srcDoc={previewNotification.html_content}
                                    title="Email Preview"
                                    className="una-preview-iframe"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationsSettings;
